import { legendDisplayName } from "@openrift/shared/card-name";
import type { SetDetailResponse } from "@openrift/shared/types/api/catalog";
import { createFileRoute, notFound } from "@tanstack/react-router";

import { NotFoundFallback, RouteErrorFallback } from "@/components/error-message";
import { publicSetDetailQueryOptions } from "@/features/cards/lib/public-sets-queries";
import { breadcrumbJsonLd, collectionPageJsonLd, seoHead } from "@/lib/seo";
import { isNotFoundSentinel } from "@/lib/server-fns/api-error";
import { getSiteUrl } from "@/lib/site-config";

export const Route = createFileRoute("/_app/sets_/$setSlug")({
  head: ({ loaderData }) => {
    const siteUrl = getSiteUrl();
    const data = loaderData as SetDetailResponse | undefined;
    if (!data) {
      return seoHead({ siteUrl, title: "Set" });
    }

    const cardCount = new Set(data.printings.map((p) => p.cardId)).size;
    const setPath = `/sets/${data.set.slug}`;
    const description = `${data.set.name} contains ${cardCount} unique cards and ${new Set(data.printings.map((p) => p.printingId)).size} printings. Browse the complete set on OpenRift.`;
    const head = seoHead({
      siteUrl,
      title: `${data.set.name} - Riftbound Card Set`,
      description,
      path: setPath,
    });

    const seenCardIds = new Set<string>();
    const items: { name: string; url: string }[] = [];
    for (const printing of data.printings) {
      if (seenCardIds.has(printing.cardId)) {
        continue;
      }
      seenCardIds.add(printing.cardId);
      const card = data.cards[printing.cardId];
      if (!card) {
        continue;
      }
      items.push({ name: legendDisplayName(card), url: `/cards/${card.slug}` });
    }

    return {
      ...head,
      scripts: [
        collectionPageJsonLd({
          siteUrl,
          name: `${data.set.name} - Riftbound Card Set`,
          description,
          path: setPath,
          items,
        }),
        breadcrumbJsonLd(siteUrl, [
          { name: "Sets", path: "/sets" },
          { name: data.set.name, path: setPath },
        ]),
      ],
    };
  },
  loader: async ({ context, params }) => {
    try {
      return await context.queryClient.query({
        ...publicSetDetailQueryOptions(params.setSlug),
        staleTime: "static",
      });
    } catch (error) {
      if (isNotFoundSentinel(error)) {
        throw notFound();
      }
      throw error;
    }
  },
  component: () => null,
  pendingComponent: () => null,
  errorComponent: RouteErrorFallback,
  notFoundComponent: NotFoundFallback,
});
